import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  MessageCircle, Copy, Check, Trash2, Edit2, X, Save,
  Zap, Camera, Upload, QrCode, Download, ZoomIn
} from "lucide-react";
import { toast } from "sonner";
import QrScanModal from "@/pages/trade/QrScanModal";
import CallPrompt from "@/pages/trade/CallPrompt";
import ContactAudioRecordButton from "@/pages/trade/ContactAudioRecordButton";
import RecordedCallButton from "@/pages/trade/RecordedCallButton";

const EMPTY = { name: "", wechat_id: "", company: "", phone: "", contact_type: "other", notes: "" };

const TYPE_LABELS = { supplier: "Supplier", agent: "Agent", other: "Contact" };

const WeChatPage = () => {
  const { API, authHeader, token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickName, setQuickName] = useState("");
  const [quickWechat, setQuickWechat] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [copiedId, setCopiedId] = useState(null);
  const [qrPreview, setQrPreview] = useState(null); // contact whose QR is enlarged
  const [qrTarget, setQrTarget] = useState(null); // contact receiving a QR upload
  const [qrScanOpen, setQrScanOpen] = useState(false);

  // Handle a decoded WeChat QR (camera scan or uploaded photo).
  // Keep the full QR content — u.wechat.com links open the person's profile directly.
  const handleQrScanned = (text) => {
    setQrScanOpen(false);
    setQuickWechat((text || "").trim());
    toast.success("QR code read — enter the name and tap Save");
  };

  // WeChat blocks direct links to a contact. QR-based IDs open the profile page;
  // plain IDs are copied to the clipboard so the user can paste them in WeChat search.
  const openWeChat = async (c) => {
    const id = (c.wechat_id || "").trim();
    const m = id.match(/https?:\/\/u\.wechat\.com\/\S+/i);
    if (m) {
      window.open(m[0], "_blank");
      return;
    }
    try { await navigator.clipboard.writeText(id); } catch {}
    window.location.href = "weixin://";
    toast.success("WeChat ID copied — in WeChat: tap + → Add Contact → paste to search", { duration: 6000 });
  };
  const qrFileRef = useRef();
  const qrCameraRef = useRef();

  const fetchData = () => {
    axios.get(`${API}/trade/wechat`, { headers: authHeader() })
      .then(res => setContacts(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const qrUrl = (c) => `${API}/trade/wechat/${c.id}/qr?auth=${token}`;

  const quickSave = async () => {
    if (!quickName.trim() || !quickWechat.trim()) {
      toast.error("Enter the name and WeChat ID");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/trade/wechat`, {
        ...EMPTY, name: quickName.trim(), wechat_id: quickWechat.trim()
      }, { headers: authHeader() });
      setContacts(prev => [res.data, ...prev]);
      setQuickName("");
      setQuickWechat("");
      toast.success(`${res.data.name} saved`);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const importExisting = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/trade/wechat/import`, {}, { headers: authHeader() });
      if (res.data.added > 0) {
        toast.success(`Imported ${res.data.added} contact${res.data.added !== 1 ? "s" : ""} with WeChat IDs`);
        fetchData();
      } else {
        toast.info("No new WeChat IDs found on suppliers or agents");
      }
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const copyWechatId = async (c) => {
    try {
      await navigator.clipboard.writeText(c.wechat_id);
      setCopiedId(c.id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("WeChat ID copied — paste it in WeChat search");
    } catch {
      toast.error("Copy failed");
    }
  };

  const openEdit = (c) => {
    setEditModal(c);
    setForm({ ...EMPTY, ...c });
  };

  const saveEdit = async () => {
    if (!form.name.trim() || !form.wechat_id.trim()) {
      toast.error("Name and WeChat ID are required");
      return;
    }
    try {
      await axios.put(`${API}/trade/wechat/${editModal.id}`, form, { headers: authHeader() });
      toast.success("Contact updated");
      setEditModal(null);
      fetchData();
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this WeChat contact?")) return;
    try {
      await axios.delete(`${API}/trade/wechat/${id}`, { headers: authHeader() });
      toast.success("Contact deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const startQrUpload = (c, camera) => {
    setQrTarget(c);
    const ref = camera ? qrCameraRef : qrFileRef;
    setTimeout(() => ref.current && ref.current.click(), 50);
  };

  const handleQrFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !qrTarget) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/trade/wechat/${qrTarget.id}/qr`, formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" }
      });
      toast.success("QR code saved");
      fetchData();
    } catch {
      toast.error("QR upload failed");
    } finally {
      setQrTarget(null);
      if (qrFileRef.current) qrFileRef.current.value = "";
      if (qrCameraRef.current) qrCameraRef.current.value = "";
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">WeChat</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">WeChat Contacts</h1>
          <p className="text-white/50 mt-2">Your China communication hub — WeChat IDs and QR codes in one place</p>
        </motion.div>

        {/* Quick add */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="trade-card rounded-sm p-6 mb-6"
          data-testid="wechat-quick-add"
        >
          <h3 className="text-sm uppercase tracking-widest trade-gold mb-4 flex items-center gap-2">
            <Zap size={14} /> Quick Save
          </h3>
          <div className="flex flex-col md:flex-row gap-3">
            <Input
              value={quickName}
              onChange={e => setQuickName(e.target.value)}
              placeholder="Name (e.g. Li Wei)"
              className="trade-input h-12 rounded-sm flex-1 text-base"
              data-testid="quick-wechat-name"
            />
            <Input
              value={quickWechat}
              onChange={e => setQuickWechat(e.target.value)}
              onKeyDown={e => e.key === "Enter" && quickSave()}
              placeholder="WeChat ID"
              className="trade-input h-12 rounded-sm flex-1 text-base"
              data-testid="quick-wechat-id"
            />
            <Button
              onClick={quickSave}
              disabled={saving}
              className="trade-btn-primary rounded-sm h-12 px-8 text-base"
              data-testid="quick-wechat-save"
            >
              <MessageCircle size={18} className="mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
          <button
            onClick={importExisting}
            disabled={importing}
            className="mt-4 text-xs trade-gold hover:underline flex items-center gap-1.5"
            data-testid="wechat-import-btn"
          >
            <Download size={12} />
            {importing ? "Importing..." : "Pull WeChat IDs from my Suppliers & Agents"}
          </button>
          <button
            onClick={() => setQrScanOpen(true)}
            className="mt-2 text-xs trade-gold hover:underline flex items-center gap-1.5"
            data-testid="wechat-scan-qr-btn"
          >
            <QrCode size={12} />
            Scan a WeChat QR code to add a contact
          </button>
        </motion.div>

        {/* hidden QR inputs */}
        <input ref={qrFileRef} type="file" accept="image/*" onChange={handleQrFile} className="hidden" data-testid="qr-file-input" />
        <input ref={qrCameraRef} type="file" accept="image/*" capture="environment" onChange={handleQrFile} className="hidden" data-testid="qr-camera-input" />

        {/* Contact list */}
        {loading ? (
          <div className="text-center p-16 text-white/40">Loading...</div>
        ) : contacts.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <MessageCircle className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No WeChat contacts yet</h3>
            <p className="text-white/50">Quick-save a contact above, or pull WeChat IDs from your suppliers and agents</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="trade-card rounded-sm p-5"
                data-testid={`wechat-card-${c.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <h3 className="font-serif font-semibold truncate">{c.name}</h3>
                    {c.company && <p className="text-xs text-white/50 truncate">{c.company}</p>}
                    <p className="text-[10px] uppercase tracking-widest trade-green mt-1">
                      {TYPE_LABELS[c.contact_type] || "Contact"}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(c)} className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors" data-testid={`edit-wechat-${c.id}`}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-2 text-red-400/60 hover:text-red-400 transition-colors" data-testid={`delete-wechat-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* WeChat ID + copy */}
                <div className="flex items-center gap-2 bg-white/5 rounded-sm px-3 py-2 mb-3">
                  <span className="text-xs font-mono text-white/80 truncate flex-1" data-testid={`wechat-id-${c.id}`}>{c.wechat_id}</span>
                  <button
                    onClick={() => copyWechatId(c)}
                    className="flex items-center gap-1 text-[11px] trade-gold hover:underline flex-shrink-0"
                    data-testid={`copy-wechat-${c.id}`}
                  >
                    {copiedId === c.id ? <Check size={12} /> : <Copy size={12} />}
                    {copiedId === c.id ? "Copied" : "Copy"}
                  </button>
                </div>

                {/* QR area */}
                {c.has_qr ? (
                  <button
                    onClick={() => setQrPreview(c)}
                    className="w-full mb-3 relative group"
                    data-testid={`qr-thumb-${c.id}`}
                  >
                    <img
                      src={qrUrl(c)}
                      alt={`${c.name} WeChat QR`}
                      className="w-full h-36 object-contain bg-white rounded-sm"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-white gap-1">
                      <ZoomIn size={14} /> Tap to enlarge
                    </span>
                  </button>
                ) : (
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => startQrUpload(c, false)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] rounded-sm bg-white/5 text-white/60 hover:bg-white/10 transition-colors"
                      data-testid={`qr-upload-${c.id}`}
                    >
                      <Upload size={12} /> Add QR
                    </button>
                    <button
                      onClick={() => startQrUpload(c, true)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] rounded-sm bg-white/5 text-white/60 hover:bg-white/10 transition-colors"
                      data-testid={`qr-camera-${c.id}`}
                    >
                      <Camera size={12} /> Scan QR
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                  <button
                    onClick={() => openWeChat(c)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                    data-testid={`open-wechat-${c.id}`}
                  >
                    <MessageCircle size={12} /> Open WeChat
                  </button>
                  {c.phone && (
                    <CallPrompt
                      phone={c.phone}
                      name={c.name}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                      testid={`call-wechat-${c.id}`}
                    />
                  )}
                  <ContactAudioRecordButton
                    phone={c.phone}
                    name={c.name}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                    testid={`audio-wechat-${c.id}`}
                  />
                  {c.phone && (
                    <RecordedCallButton
                      phone={c.phone}
                      name={c.name}
                      contactId={c.id}
                      contactType="wechat"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                      testid={`record-call-wechat-${c.id}`}
                    />
                  )}
                  {c.has_qr && (
                    <button
                      onClick={() => startQrUpload(c, false)}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                      data-testid={`qr-replace-${c.id}`}
                    >
                      <QrCode size={12} /> Replace QR
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* QR scan modal */}
        {qrScanOpen && (
          <QrScanModal onClose={() => setQrScanOpen(false)} onScan={handleQrScanned} />
        )}

        {/* QR preview modal */}
        {qrPreview && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setQrPreview(null)}
            data-testid="qr-preview-modal"
          >
            <div className="max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <img src={qrUrl(qrPreview)} alt="WeChat QR" className="w-full bg-white rounded-sm" />
              <p className="text-center text-sm text-white/70 mt-3">{qrPreview.name} — scan with WeChat to add</p>
              <Button onClick={() => setQrPreview(null)} className="w-full mt-4 trade-btn-primary rounded-sm" data-testid="qr-preview-close">
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Edit modal */}
        {editModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setEditModal(null)}
            data-testid="wechat-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif font-semibold text-lg">Edit WeChat Contact</h3>
                <button onClick={() => setEditModal(null)} className="p-2 text-white/40 hover:text-white transition-colors" data-testid="wechat-modal-close">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Name *</Label>
                    <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="wechat-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">WeChat ID *</Label>
                    <Input value={form.wechat_id} onChange={e => setForm({...form, wechat_id: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="wechat-id-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Company</Label>
                    <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="wechat-company-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="wechat-phone-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-white/60">Type</Label>
                    <Select value={form.contact_type} onValueChange={v => setForm({...form, contact_type: v})}>
                      <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="wechat-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#142039] border-white/10">
                        <SelectItem value="supplier">Supplier</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="other">Other Contact</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Notes</Label>
                  <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} placeholder="What they supply, prices discussed..." className="trade-input mt-1 rounded-sm" data-testid="wechat-notes-input" />
                </div>
                <Button onClick={saveEdit} className="w-full trade-btn-primary rounded-sm h-12" data-testid="save-wechat-btn">
                  <Save size={16} className="mr-2" /> Save Contact
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeChatPage;
